import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'

let faroInstance = null

export function initFaro() {
    const url = import.meta.env.VITE_FARO_URL
    if (!url || faroInstance) {
        return
    }

    try {
        faroInstance = initializeFaro({
            url,
            app: {
                name: 'power-frontend',
                version: import.meta.env.VITE_APP_VERSION || '0.0.0',
                environment: import.meta.env.MODE
            },
            instrumentations: [
                ...getWebInstrumentations(),
                new TracingInstrumentation({
                    instrumentationOptions: {
                        propagateTraceHeaderCorsUrls: [
                            new RegExp('^' + window.location.origin)
                        ]
                    }
                })
            ]
        })
    } catch (_err) {
        // Faro failure must never break app boot
    }
}

/**
 * Emit a custom Faro event. No-op when Faro is not initialised (VITE_FARO_URL unset).
 * Attributes must contain only non-PII domain values (ids of things, counts, types).
 *
 * @param {string} name - Event name
 * @param {Record<string, string>} [attributes={}] - Non-PII attributes
 */
export function trackEvent(name, attributes = {}) {
    try {
        faroInstance?.api?.pushEvent(name, attributes)
    } catch (_err) {
        // Never throw — telemetry must not affect app behaviour
    }
}

/**
 * Report a caught error to telemetry. Used by React error boundaries so a
 * crashed component is logged to Faro (and always to the console) instead of
 * silently swallowed. No-op for Faro when not initialised (VITE_FARO_URL unset).
 *
 * @param {Error} error - The thrown error caught by an error boundary
 * @param {Record<string, unknown>} [context={}] - Non-PII context (boundary
 *        label, React componentStack, etc.)
 */
export function reportError(error, context = {}) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, context)
    try {
        faroInstance?.api?.pushError(error, { context })
    } catch (_err) {
        // Never throw — telemetry must not affect app behaviour, least of all
        // from inside an error handler.
    }
}

/**
 * Replace dynamic URL segments (numeric ids, UUIDs, long hex hashes) with `:id`
 * so `/users/123/profile` and `/users/456/profile` aggregate as a single view
 * and no per-record identifiers land in telemetry attributes.
 *
 * @param {string} pathname - Raw `location.pathname`
 * @returns {string} Normalised path suitable for use as a Faro view name
 */
function normalizeViewName(pathname) {
    return pathname
        .replace(/\/\d+(?=\/|$)/g, '/:id')
        .replace(/\/[0-9a-f]{8,}(?:-[0-9a-f]{4,}){0,4}(?=\/|$)/gi, '/:id')
}

/**
 * Set the current Faro view. `setView` tags every subsequent signal with
 * `view.name` and emits a `view_changed` event when the name changes — enough
 * to answer "who opened page X" in Loki without any extra `pushEvent` call.
 *
 * @param {string} pathname - Raw `location.pathname` (will be normalised)
 */
export function setFaroView(pathname) {
    if (!faroInstance || !pathname) {
        return
    }
    try {
        faroInstance.api.setView({ name: normalizeViewName(pathname) })
    } catch (_err) {
        // Never throw — telemetry must not affect app behaviour
    }
}

/**
 * Associate telemetry with a staff user identifier.
 * Pass `user.telemetryId` from the backend — the backend returns the raw user
 * UUID for staff and an empty string for pupils. Pupils therefore become a
 * no-op here, so their navigations stay unattributed in Faro / Loki. Never
 * call this with emails, names, or pupil identifiers directly.
 *
 * @param {string} pseudoId - Backend-supplied identifier (staff UUID, or '' for
 *                            pupils / anonymous → no-op)
 */
export function setPseudonymousUser(pseudoId) {
    if (!faroInstance || !pseudoId) {
        return
    }
    try {
        faroInstance.api.setUser({ id: pseudoId })
    } catch (_err) {
        // Never throw
    }
}

export default initFaro
